import { Directionality } from "@angular/cdk/bidi";
import { Dialog, DialogConfig } from "@angular/cdk/dialog";
import { Overlay, OverlayConfig, OverlayContainer, OverlayRef, ScrollStrategy } from "@angular/cdk/overlay";
import { ComponentPortal, ComponentType, TemplatePortal } from "@angular/cdk/portal";
import { Location } from "@angular/common";
import {
	Directive,
	Inject,
	Injectable,
	InjectionToken,
	Injector,
	OnDestroy,
	Optional,
	SkipSelf,
	StaticProvider,
	TemplateRef,
	Type,
} from "@angular/core";
import {
	_MatLegacyDialogContainerBase as _MatDialogContainerBase,
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MAT_LEGACY_DIALOG_SCROLL_STRATEGY as MAT_DIALOG_SCROLL_STRATEGY,
	MatLegacyDialogConfig as MatDialogConfig,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { defer, Observable, of as observableOf, Subject } from "rxjs";
import { startWith } from "rxjs/operators";
import { MatSideSheetComponent } from "./mat-side-sheet.component";

export const MAT_SHEET_DEFAULT_OPTIONS = new InjectionToken<MatDialogConfig>("mat-sheet-default-options");

let uniqueId = 0;
/**
 * Base class for dialog services. The base dialog service allows
 * for arbitrary dialog refs and dialog container components.
 */
@Directive()
export abstract class _MatSheetBase<C extends _MatDialogContainerBase> implements OnDestroy {
	private _openDialogsAtThisLevel: MatDialogRef<any>[] = [];
	private readonly _afterAllClosedAtThisLevel = new Subject<void>();
	private readonly _afterOpenedAtThisLevel = new Subject<MatDialogRef<any>>();
	private _ariaHiddenElements = new Map<Element, string | null>();
	private _scrollStrategy: () => ScrollStrategy;
	private _dialog: Dialog;
	protected _idPrefix = "mat-sheet-";

	/** Keeps track of the currently-open dialogs. */
	get openDialogs(): MatDialogRef<any>[] {
		return this._parentDialog ? this._parentDialog.openDialogs : this._openDialogsAtThisLevel;
	}

	/** Stream that emits when a dialog has been opened. */
	get afterOpened(): Subject<MatDialogRef<any>> {
		return this._parentDialog ? this._parentDialog.afterOpened : this._afterOpenedAtThisLevel;
	}

	_getAfterAllClosed(): Subject<void> {
		const parent = this._parentDialog;
		return parent ? parent._getAfterAllClosed() : this._afterAllClosedAtThisLevel;
	}

	// TODO (jelbourn): tighten the typing right-hand side of this expression.
	/**
	 * Stream that emits when all open dialog have finished closing.
	 * Will emit on subscribe if there are no open dialogs to begin with.
	 */
	readonly afterAllClosed: Observable<void> = defer(() =>
		this.openDialogs.length ? this._getAfterAllClosed() : this._getAfterAllClosed().pipe(startWith(undefined))
	) as Observable<any>;

	constructor(
		private _overlay: Overlay,
		private _injector: Injector,
		private _defaultOptions: MatDialogConfig | undefined,
		private _parentDialog: _MatSheetBase<C> | undefined,
		private _overlayContainer: OverlayContainer,
		scrollStrategy: any,
		private _dialogRefConstructor: Type<MatDialogRef<any>>,
		private _dialogContainerType: Type<C>,
		private _dialogDataToken: InjectionToken<any>
	) {
		this._scrollStrategy = scrollStrategy;
		this._dialog = _injector.get(Dialog);
	}

	/**
	 * Opens a modal dialog containing the given component.
	 * @param component Type of the component to load into the dialog.
	 * @param config Extra configuration options.
	 * @returns Reference to the newly-opened dialog.
	 */
	open<T, D = any, R = any>(component: ComponentType<T>, config?: MatDialogConfig<D>): MatDialogRef<T, R>;

	/**
	 * Opens a modal dialog containing the given template.
	 * @param template TemplateRef to instantiate as the dialog content.
	 * @param config Extra configuration options.
	 * @returns Reference to the newly-opened dialog.
	 */
	open<T, D = any, R = any>(template: TemplateRef<T>, config?: MatDialogConfig<D>): MatDialogRef<T, R>;

	open<T, D = any, R = any>(
		template: ComponentType<T> | TemplateRef<T>,
		config?: MatDialogConfig<D>
	): MatDialogRef<T, R>;

	open<T, D = any, R = any>(
		componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
		config?: MatDialogConfig<D>
	): MatDialogRef<T, R> {
		let dialogRef: MatDialogRef<T, R>;
		config = { ...(this._defaultOptions || new MatDialogConfig()), ...config };
		config.id = config.id || `${this._idPrefix}${uniqueId++}`;
		config.scrollStrategy = config.scrollStrategy || this._scrollStrategy();

		const cdkRef = this._dialog.open<R, D, T>(componentOrTemplateRef, {
			height: "100%",
			...config,
			positionStrategy: this._overlay.position().global().left("0").top("0"),
			// Disable closing since we need to sync it up to the animation ourselves.
			disableClose: true,
			// Disable closing on destroy, because this service cleans up its open dialogs as well.
			// We want to do the cleanup here, rather than the CDK service, because the CDK destroys
			// the dialogs immediately whereas we want it to wait for the animations to finish.
			closeOnDestroy: false,
			container: {
				type: this._dialogContainerType,
				providers: () => [
					// Provide our config as the CDK config as well since it has the same interface as the
					// CDK one, but it contains the actual values passed in by the user for things like
					// `disableClose` which we disable for the CDK dialog since we handle it ourselves.
					{ provide: MatDialogConfig, useValue: config },
					{ provide: DialogConfig, useValue: config },
				],
			},
			templateContext: () => ({ dialogRef }),
			providers: (ref, cdkConfig, dialogContainer) => {
				dialogRef = new this._dialogRefConstructor(ref, config, dialogContainer);
				dialogRef.updatePosition(config?.position);
				return [
					{ provide: this._dialogContainerType, useValue: dialogContainer },
					{ provide: this._dialogDataToken, useValue: cdkConfig.data },
					{ provide: this._dialogRefConstructor, useValue: dialogRef },
				];
			},
		});

		// This can't be assigned in the `providers` callback, because
		// the instance hasn't been assigned to the CDK ref yet.
		dialogRef!.componentInstance = cdkRef.componentInstance!;

		this.openDialogs.push(dialogRef!);
		this.afterOpened.next(dialogRef!);

		dialogRef!.afterClosed().subscribe(() => {
			const index = this.openDialogs.indexOf(dialogRef);

			if (index > -1) {
				this.openDialogs.splice(index, 1);

				if (!this.openDialogs.length) {
					this._getAfterAllClosed().next();
				}
			}
		});

		return dialogRef!;
	}

	/**
	 * Closes all of the currently-open dialogs.
	 */
	closeAll(): void {
		this._closeDialogs(this.openDialogs);
	}

	/**
	 * Finds an open dialog by its id.
	 * @param id ID to use when looking up the dialog.
	 */
	getDialogById(id: string): MatDialogRef<any> | undefined {
		return this.openDialogs.find((dialog) => dialog.id === id);
	}

	ngOnDestroy() {
		// Only close the dialogs at this level on destroy
		// since the parent service may still be active.
		this._closeDialogs(this._openDialogsAtThisLevel);
		this._afterAllClosedAtThisLevel.complete();
		this._afterOpenedAtThisLevel.complete();
	}

	/**
	 * Creates the overlay into which the dialog will be loaded.
	 * @param config The dialog configuration.
	 * @returns A promise resolving to the OverlayRef for the created overlay.
	 */
	private _createOverlay(config: MatDialogConfig): OverlayRef {
		const overlayConfig = this._getOverlayConfig(config);
		return this._overlay.create(overlayConfig);
	}

	/**
	 * Creates an overlay config from a dialog config.
	 * @param dialogConfig The dialog configuration.
	 * @returns The overlay configuration.
	 */
	private _getOverlayConfig(dialogConfig: MatDialogConfig): OverlayConfig {
		const state = new OverlayConfig({
			positionStrategy: this._overlay.position().global(),
			scrollStrategy: dialogConfig.scrollStrategy || this._scrollStrategy(),
			panelClass: dialogConfig.panelClass,
			hasBackdrop: dialogConfig.hasBackdrop,
			direction: dialogConfig.direction,
			minWidth: dialogConfig.minWidth,
			minHeight: dialogConfig.minHeight,
			maxWidth: dialogConfig.maxWidth,
			maxHeight: dialogConfig.maxHeight,
			disposeOnNavigation: dialogConfig.closeOnNavigation,
		});

		if (dialogConfig.backdropClass) {
			state.backdropClass = dialogConfig.backdropClass;
		}

		return state;
	}

	/**
	 * Attaches a dialog container to a dialog's already-created overlay.
	 * @param overlay Reference to the dialog's underlying overlay.
	 * @param config The dialog configuration.
	 * @returns A promise resolving to a ComponentRef for the attached container.
	 */
	private _attachDialogContainer(overlay: OverlayRef, config: MatDialogConfig): C {
		const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
		const injector = Injector.create({
			parent: userInjector || this._injector,
			providers: [{ provide: MatDialogConfig, useValue: config }],
		});

		const containerPortal = new ComponentPortal(
			this._dialogContainerType,
			config.viewContainerRef,
			injector,
			config.componentFactoryResolver
		);
		const containerRef = overlay.attach<C>(containerPortal);

		return containerRef.instance;
	}

	/**
	 * Attaches the user-provided component to the already-created dialog container.
	 * @param componentOrTemplateRef The type of component being loaded into the dialog,
	 *     or a TemplateRef to instantiate as the content.
	 * @param dialogContainer Reference to the wrapping dialog container.
	 * @param overlayRef Reference to the overlay in which the dialog resides.
	 * @param config The dialog configuration.
	 * @returns A promise resolving to the MatSideSheetRef that should be returned to the user.
	 */
	private _attachDialogContent<T, R>(
		componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
		dialogContainer: C,
		overlayRef: OverlayRef,
		config: MatDialogConfig
	): MatDialogRef<T, R> {
		// Create a reference to the dialog we're creating in order to give the user a handle
		// to modify and close it.
		const dialogRef = new this._dialogRefConstructor(overlayRef, dialogContainer, config.id);

		if (componentOrTemplateRef instanceof TemplateRef) {
			dialogContainer.attachTemplatePortal(
				new TemplatePortal<T>(componentOrTemplateRef, null!, <any>{ $implicit: config.data, dialogRef })
			);
		} else {
			const injector = this._createInjector<T>(config, dialogRef, dialogContainer);
			const contentRef = dialogContainer.attachComponentPortal<T>(
				new ComponentPortal(componentOrTemplateRef, config.viewContainerRef, injector)
			);
			dialogRef.componentInstance = contentRef.instance;
		}

		dialogRef.updateSize(config.width, config.height).updatePosition(config.position);

		return dialogRef;
	}

	/**
	 * Creates a custom injector to be used inside the dialog. This allows a component loaded inside
	 * of a dialog to close itself and, optionally, to return a value.
	 * @param config Config object that is used to construct the dialog.
	 * @param dialogRef Reference to the dialog.
	 * @param dialogContainer Dialog container element that wraps all of the contents.
	 * @returns The custom injector that can be used inside the dialog.
	 */
	private _createInjector<T>(config: MatDialogConfig, dialogRef: MatDialogRef<T>, dialogContainer: C): Injector {
		const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;

		// The dialog container should be provided as the dialog container and the dialog's
		// content are created out of the same `ViewContainerRef` and as such, are siblings
		// for injector purposes. To allow the hierarchy that is expected, the dialog
		// container is explicitly provided in the injector.
		const providers: StaticProvider[] = [
			{ provide: this._dialogContainerType, useValue: dialogContainer },
			{ provide: this._dialogDataToken, useValue: config.data },
			{ provide: this._dialogRefConstructor, useValue: dialogRef },
		];

		if (config.direction && (!userInjector || !userInjector.get<Directionality | null>(Directionality, null))) {
			providers.push({
				provide: Directionality,
				useValue: { value: config.direction, change: observableOf() },
			});
		}

		return Injector.create({ parent: userInjector || this._injector, providers });
	}

	/**
	 * Removes a dialog from the array of open dialogs.
	 * @param dialogRef Dialog to be removed.
	 */
	private _removeOpenDialog(dialogRef: MatDialogRef<any>) {
		const index = this.openDialogs.indexOf(dialogRef);

		if (index > -1) {
			this.openDialogs.splice(index, 1);

			// If all the dialogs were closed, remove/restore the `aria-hidden`
			// to a the siblings and emit to the `afterAllClosed` stream.
			if (!this.openDialogs.length) {
				this._ariaHiddenElements.forEach((previousValue, element) => {
					if (previousValue) {
						element.setAttribute("aria-hidden", previousValue);
					} else {
						element.removeAttribute("aria-hidden");
					}
				});

				this._ariaHiddenElements.clear();
				this._getAfterAllClosed().next();
			}
		}
	}

	/**
	 * Hides all of the content that isn't an overlay from assistive technology.
	 */
	private _hideNonDialogContentFromAssistiveTechnology() {
		const overlayContainer = this._overlayContainer.getContainerElement();

		// Ensure that the overlay container is attached to the DOM.
		if (overlayContainer.parentElement) {
			const siblings = overlayContainer.parentElement.children;

			for (let i = siblings.length - 1; i > -1; i--) {
				let sibling = siblings[i];

				if (
					sibling !== overlayContainer &&
					sibling.nodeName !== "SCRIPT" &&
					sibling.nodeName !== "STYLE" &&
					!sibling.hasAttribute("aria-live")
				) {
					this._ariaHiddenElements.set(sibling, sibling.getAttribute("aria-hidden"));
					sibling.setAttribute("aria-hidden", "true");
				}
			}
		}
	}

	/** Closes all of the dialogs in an array. */
	private _closeDialogs(dialogs: MatDialogRef<any>[]) {
		let i = dialogs.length;

		while (i--) {
			// The `_openDialogs` property isn't updated after close until the rxjs subscription
			// runs on the next microtask, in addition to modifying the array as we're going
			// through it. We loop through all of them and call close without assuming that
			// they'll be removed from the list instantaneously.
			dialogs[i].close();
		}
	}
}

/**
 * Service to open Material Design modal dialogs.
 */
@Injectable()
export class MatSideSheet extends _MatSheetBase<MatSideSheetComponent> {
	constructor(
		overlay: Overlay,
		injector: Injector,
		/**
		 * @deprecated `_location` parameter to be removed.
		 * @breaking-change 10.0.0
		 */
		@Optional() location: Location,
		@Optional() @Inject(MAT_SHEET_DEFAULT_OPTIONS) defaultOptions: MatDialogConfig,
		@Inject(MAT_DIALOG_SCROLL_STRATEGY) scrollStrategy: any,
		@Optional() @SkipSelf() parentDialog: MatSideSheet,
		overlayContainer: OverlayContainer
	) {
		super(
			overlay,
			injector,
			defaultOptions,
			parentDialog,
			overlayContainer,
			scrollStrategy,
			MatDialogRef,
			MatSideSheetComponent,
			MAT_DIALOG_DATA
		);
	}
}

/**
 * Applies default options to the dialog config.
 * @param config Config to be modified.
 * @param defaultOptions Default options provided.
 * @returns The new configuration object.
 */
function _applyConfigDefaults(config?: MatDialogConfig, defaultOptions?: MatDialogConfig): MatDialogConfig {
	return { ...defaultOptions, ...config };
}
